// The intention of this class is to load in data that will be used across many different components.
// These values are ones that can be loaded when a user first logs in and are then needed throughout the application.
/* eslint-disable consistent-return */
import Config from '../components/graphql/GraphQL.config';
import ApolloClient from 'apollo-client';
import { InMemoryCache } from 'apollo-cache-inmemory';
import _find from 'lodash/find';
import {
  asyncQuery,
  getGlobalProps,
  setGlobalProps
} from '../utils/queryHelpers';
import { userInfoOrgAccess } from '../graphQL/queries/userQueries';
import {
  userInfo,
  defaultBrandingInfo,
  getSearchBarUpdates
} from '../graphQL/queries/globalQueries';
import {
  branding,
  organizationTypesRefData
} from '../graphQL/queries/orgQueries';
import {
  initialRefs,
  initialOrgRefs
} from '../graphQL/queries/referenceDataQueries';

import { all as orgAccessorialServicesQuery } from '../graphQL/queries/orgRefOrgAccessorialService';
import { buildDropdown } from '../utils/Shipment';
import { HELPSCOUT_ACCOUNT } from '../constants/constants';

export default class GlobalProps {
  constructor() {
    const link = new Config().getConfig();
    this.client = new ApolloClient({
      clientState: {},
      link,
      cache: new InMemoryCache()
    });
  }

  formatUser = (user, orgId) => {
    const { orgUsers } = user;
    // find the matching orgId to get the user's role
    const myOrg = _find(orgUsers, function(ou) {
      return orgId && ou.orgId.toString() === orgId.toString();
    });

    const retUser = {
      firstName: user.firstName,
      lastName: user.lastName,
      id: user.id,
      loginEmail: user.loginEmail,
      timeZoneId: user.timeZoneId,
      roleId: myOrg ? myOrg.roleId : null,
      orgUsers
    };

    return retUser;
  };

  getSearchBarUpdates = async orgId => {
    const { data } = await this.client.query({
      query: getSearchBarUpdates,
      variables: { orgId: orgId },
      fetchPolicy: 'network-only'
    });
    return Promise.resolve(data);
  };

  setupOrganization = async (orgId, currentGlobalData, resync = false) => {
    const orgDataCache = getGlobalProps();
    if (orgDataCache && orgDataCache.currentOrgId === orgId && !resync) {
      return orgDataCache;
    }

    try {
      const {
        organization: {
          byId: { mainBusiness, config, name: orgName }
        },
        userAccess: { permissions }
      } = await asyncQuery(this.client, userInfo, {
        id: orgId
      });

      const {
        orgRef_OrgAccessorialService: { all: orgAccessorialServices }
      } = await asyncQuery(this.client, orgAccessorialServicesQuery, {
        orgId: orgId
      });

      const {
        orgRef_GlCode: { all: glCodes },
        orgRef_LcpExceptionReason: { all: lcpExceptions }
      } = await asyncQuery(this.client, initialOrgRefs, {
        orgId: orgId
      });

      let subscriptionID, messagingEnabled, chatEnabled, messagingMode;
      //TODO:  helpScoutSubscription needs to be moved into branding.  Implement when additional branding customer arrives.
      if (config.branding.id === HELPSCOUT_ACCOUNT.Ward.brandingID) {
        subscriptionID = HELPSCOUT_ACCOUNT.Ward.subscriptionID;
        messagingEnabled = HELPSCOUT_ACCOUNT.Ward.messagingEnabled;
        chatEnabled = HELPSCOUT_ACCOUNT.Ward.chatEnabled;
        messagingMode = HELPSCOUT_ACCOUNT.Ward.messagingMode;
      } else {
        subscriptionID = HELPSCOUT_ACCOUNT.Default.subscriptionID;
        messagingEnabled = HELPSCOUT_ACCOUNT.Default.messagingEnabled;
        chatEnabled = HELPSCOUT_ACCOUNT.Default.chatEnabled;
        messagingMode = HELPSCOUT_ACCOUNT.Default.messagingMode;
      }

      const globalData = {
        ...currentGlobalData,
        user: this.formatUser(currentGlobalData.user, orgId),
        permissions,
        orgRefs: {
          glCodes,
          accessorials: buildDropdown(
            orgAccessorialServices,
            'name',
            'accessorialServiceId'
          ),
          lcpExceptions: buildDropdown(lcpExceptions, 'name', 'id')
        },
        orgInfo: {
          orgId: orgId,
          businessId: mainBusiness.id,
          divisionId: mainBusiness.rootDivision.id,
          config,
          orgName
        },
        branding: config.branding,
        currentOrgId: orgId,
        hasMultipleOrgs:
          currentGlobalData.isPropelAdministrator ||
          currentGlobalData.userOrganizations.length > 1,
        helpScout: {
          subscriptionID: subscriptionID,
          messagingEnabled: messagingEnabled,
          chatEnabled: chatEnabled,
          messagingMode: messagingMode
        }
      };

      setGlobalProps(globalData);
      return Promise.resolve(globalData);
    } catch (error) {
      return Promise.reject(error);
    }
  };

  getProps = async () => {
    // first check if we have the data already
    const orgDataCache = getGlobalProps();
    if (orgDataCache) {
      return orgDataCache;
    }

    //not there, need to query
    try {
      const {
        userAccess: { me }
      } = await asyncQuery(this.client, userInfoOrgAccess);
      const { isPropelAdministrator, visibleOrganizations } = me;

      const { referenceData: initialRefData } = await asyncQuery(
        this.client,
        initialRefs
      );
      // Set up the global data object and save it to sessionStorage.
      // Massage the reference data into the form we need for dropdowns so that each
      // form doesn't need to do that themselves
      const { eulaAccepted } = me.user;
      let globalData = {
        user: this.formatUser(me.user, null),
        eulaAccepted,
        referenceData: {
          accessorialServices: buildDropdown(
            initialRefData.accessorialServices
          ),
          equipmentTypes: buildDropdown(initialRefData.equipmentTypes),
          freightClasses: buildDropdown(initialRefData.freightClasses),
          hazMatClasses: buildDropdown(initialRefData.hazMatClasses),
          hazMatPackingGroups: buildDropdown(
            initialRefData.hazMatPackingGroups
          ),
          packageTypes: buildDropdown(initialRefData.packageTypes),
          serviceLevels: buildDropdown(initialRefData.serviceLevels),
          shipmentDirections: buildDropdown(initialRefData.shipmentDirections),
          shipmentModes: buildDropdown(initialRefData.shipmentModes),
          shipmentStatuses: initialRefData.shipmentStatuses,
          fileTypes: buildDropdown(initialRefData.fileTypes),
          filePurposes: buildDropdown(initialRefData.filePurposes),
          shipmentDocumentTypes: buildDropdown(
            initialRefData.shipmentDocumentTypes
          ),
          notificationReasons: buildDropdown(
            initialRefData.notificationReasons
          ),
          customFieldTypes: buildDropdown(initialRefData.customFieldTypes),
          timeZones: buildDropdown(initialRefData.timeZones),
          unitTypes: buildDropdown(initialRefData.unitTypes),
          cultures: buildDropdown(initialRefData.cultures),
          country: buildDropdown(initialRefData.country),
          currencies: buildDropdown(initialRefData.currencies),
          rateProviders: buildDropdown(initialRefData.rateProviders),
          netTerms: buildDropdown(initialRefData.netTermses),
          laneTypes: initialRefData.laneTypes
        },
        isPropelAdministrator,
        currentOrgId: null,
        hasMultipleOrgs: isPropelAdministrator,
        userOrganizations: visibleOrganizations.map(org => {
          return { id: org.id, name: org.name };
        }),
        helpScout: {
          subscriptionID: HELPSCOUT_ACCOUNT.Default.subscriptionID,
          messagingEnabled: HELPSCOUT_ACCOUNT.Default.messagingEnabled,
          chatEnabled: HELPSCOUT_ACCOUNT.Default.chatEnabled,
          messagingMode: HELPSCOUT_ACCOUNT.Default.messagingMode
        }
      };

      if (isPropelAdministrator) {
        // Load up system branding
        const {
          branding: { defaultBranding }
        } = await asyncQuery(this.client, defaultBrandingInfo);

        // Load up branding options
        const {
          branding: { all: brandingOptions }
        } = await asyncQuery(this.client, branding);

        // Load up Organization Types
        const {
          referenceData: { organizationTypes }
        } = await asyncQuery(this.client, organizationTypesRefData);

        globalData = {
          ...globalData,
          branding: defaultBranding,
          brandingOptions,
          organizationTypes
        };
      }

      //TODO - check here to see if there are no available orgs.
      // If there are not and admin is true then we'll want to route them to
      // a Propel Admin page where they can pick an org to use.
      // If they aren't an admin we should show a modal saying they don't have access
      // where the only option is to say OK which logs them out.

      // If the User has Orgs, default to the first one
      if (visibleOrganizations.length > 0) {
        return await this.setupOrganization(
          visibleOrganizations[0].id,
          globalData
        );
      }
      setGlobalProps(globalData);
      return Promise.resolve(globalData);
    } catch (error) {
      return Promise.reject(error);
    }
  };
}
